import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Trash2, Pencil, Plus } from "lucide-react";
import AdminDialog from "../../components/admin/AdminDialog";

const TYPES = [
  { value: "carousel", label: "Banner Carousel", configHint: '{"banner_ids": ["id1","id2"]}' },
  { value: "product_grid", label: "Product Grid", configHint: '{"query": {"is_featured": true}, "limit": 8}' },
  { value: "category_grid", label: "Category Grid (Featured)", configHint: '{"category_ids": ["id1","id2"]}' },
  { value: "brand_story", label: "Brand Story Block", configHint: '{"image_url": "...", "heading": "...", "body": "..."}' },
  { value: "banner_strip", label: "Banner Strip", configHint: '{"banner_ids": ["id"]}' },
  { value: "announcement_bar", label: "Announcement Bar", configHint: '{"placement": "announcement"}  (uses all active banners with that placement)' },
  { value: "coupon_banner", label: "Coupon Banner", configHint: '{"heading":"FESTIVE25","body":"Flat 25% off","coupon_code":"FESTIVE25","cta_label":"Shop Now","cta_url":"/shop"}' },
  { value: "seo_block", label: "SEO Content Block", configHint: '{"heading":"Why GetOwnApp Commerce","body":"...","columns":[{"heading":"...","body":"..."}]}' },
  { value: "trending_products", label: "Trending Products", configHint: '{"query": {"is_trending": true}, "limit": 8, "sort": "review_count"}' },
  { value: "featured_collection", label: "Featured Collection", configHint: '{"query": {"ids": ["productId1","productId2"]}, "limit": 6}' },
];

const blank = { title: "", slug: "", type: "product_grid", config: {}, order: 0, is_active: true, start_at: "", end_at: "" };
const slugify = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

export default function AdminSections() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [form, setForm] = useState(blank);
  const [configText, setConfigText] = useState("{}");
  const [editingId, setEditingId] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/admin/sections");
      setRows(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load sections");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditingId(null);
    setForm(blank);
    setConfigText("{}");
    setDialogOpen(true);
  };
  const openEdit = (s) => {
    setEditingId(s.id);
    setForm({ ...blank, ...s, start_at: s.start_at || "", end_at: s.end_at || "" });
    setConfigText(JSON.stringify(s.config || {}, null, 2));
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.title) return toast.error("Title required");
    let config;
    try { config = JSON.parse(configText || "{}"); }
    catch { return toast.error("Config must be valid JSON"); }
    const body = { ...form, slug: form.slug || slugify(form.title), config, order: Number(form.order) || 0 };
    if (!body.start_at) delete body.start_at;
    if (!body.end_at) delete body.end_at;
    try {
      if (editingId) await api.put(`/admin/sections/${editingId}`, body);
      else await api.post("/admin/sections", body);
      toast.success(editingId ? "Updated" : "Created");
      setDialogOpen(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this section?")) return;
    await api.delete(`/admin/sections/${id}`);
    load();
  };

  const typeMeta = TYPES.find((t) => t.value === form.type) || TYPES[0];

  return (
    <div data-testid="admin-sections-page">
      <div className="flex justify-between items-start mb-6 flex-wrap gap-3">
        <div>
          <div className="overline mb-2">Marketing</div>
          <h1 className="font-display text-4xl">Homepage Sections</h1>
          <p className="text-sm text-[hsl(var(--kb-muted))] mt-1">Collections, featured grids, brand blocks, promotional carousels.</p>
        </div>
        <button className="kb-btn-primary" onClick={openNew} data-testid="section-add-btn">
          <Plus size={16} className="inline mr-1" /> New Section
        </button>
      </div>

      <div className="bg-white border border-[hsl(var(--kb-border))] overflow-x-auto">
        {loading ? (
          <div className="p-8 text-center text-[hsl(var(--kb-muted))]" data-testid="sections-loading">Loading sections…</div>
        ) : error ? (
          <div className="p-8 text-center text-red-700" data-testid="sections-error">{error}</div>
        ) : rows.length === 0 ? (
          <div className="p-8 text-center text-[hsl(var(--kb-muted))]">No sections yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[hsl(var(--kb-surface-alt))] text-left text-[hsl(var(--kb-muted))]">
              <tr><th className="p-3">Order</th><th>Title</th><th>Type</th><th>Slug</th><th>Status</th><th></th></tr>
            </thead>
            <tbody>
              {rows.map((s) => (
                <tr key={s.id} className="border-t border-[hsl(var(--kb-border))]" data-testid={`section-row-${s.slug}`}>
                  <td className="p-3 font-mono">{s.order}</td>
                  <td className="p-3 font-medium">{s.title}</td>
                  <td className="p-3"><span className="chip">{s.type}</span></td>
                  <td className="p-3 text-xs font-mono">{s.slug}</td>
                  <td className="p-3 text-xs uppercase">{s.is_active ? "active" : "inactive"}</td>
                  <td className="p-3">
                    <div className="flex gap-2">
                      <button onClick={() => openEdit(s)} className="kb-btn-outline !py-1 !px-2 !text-[10px]" data-testid={`section-edit-${s.slug}`}><Pencil size={12} /></button>
                      <button onClick={() => remove(s.id)} className="text-red-700 p-1" data-testid={`section-del-${s.slug}`}><Trash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <AdminDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title="Homepage Section"
        subtitle={editingId ? "Edit" : "New"}
        maxWidth="max-w-2xl"
        testid="section-dialog"
        footer={
          <>
            <button className="kb-btn-outline" onClick={() => setDialogOpen(false)}>Cancel</button>
            <button className="kb-btn-primary" onClick={save} data-testid="section-save-btn">{editingId ? "Update" : "Create"}</button>
          </>
        }
      >
        <div className="grid md:grid-cols-2 gap-3">
          <input className="kb-input" placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="section-title" />
          <input className="kb-input" placeholder="Slug (auto)" value={form.slug} onChange={(e) => setForm({ ...form, slug: slugify(e.target.value) })} />
          <select className="kb-input" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="section-type">
            {TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
          <input type="number" className="kb-input" placeholder="Order" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} />
          <input type="date" className="kb-input" value={form.start_at} onChange={(e) => setForm({ ...form, start_at: e.target.value })} />
          <input type="date" className="kb-input" value={form.end_at} onChange={(e) => setForm({ ...form, end_at: e.target.value })} />
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} /> Active
        </label>
        <div>
          <label className="overline block mb-1">Config (JSON)</label>
          <textarea rows={8} className="kb-input font-mono text-xs" value={configText} onChange={(e) => setConfigText(e.target.value)} data-testid="section-config" />
          <p className="text-xs text-[hsl(var(--kb-muted))] mt-1">Example for <b>{typeMeta.label}</b>: <code>{typeMeta.configHint}</code></p>
        </div>
      </AdminDialog>
    </div>
  );
}
